import * as React from 'react'
import {
  Box,
  Modal,
  Typography as MIUTypography,
  Button,
  TextField,
  useTheme,
  useMediaQuery,
  Alert,
  Divider,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Warning, Delete, Cancel, CheckCircle } from '@mui/icons-material'
import { Snackbar } from '@mui/material'
import * as RT from '../../config/routeType'
import { detectCityFromURL } from '../../utils/cityDetector'
import { buildCantonUrl } from '../../utils/cantonUrl'
import { useCities } from '../../context/CitiesContext'
import { styled } from '@mui/material/styles'
import { useSelector, useDispatch } from 'react-redux'
import { setStateModal } from '../../redux/slices/Modal/modalDeleteRecord'
import { deleteTree } from '../../services'
import { invalidateMapData } from '../../redux/slices/Tree/treeFeatures'
import { clearFilteredData, setEnabledFilter } from '../../redux/slices/Search/input'
import { setPopup } from '../../redux/slices/Map/map'
import { useLocation } from 'react-router-dom'

const Typography = styled(MIUTypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: props.istitle ? '1.3em' : '0.95em',
  fontWeight: props.istitle ? 600 : 400,
  lineHeight: props.istitle ? '28px' : '22px',
  color: props.istitle ? '#d32f2f' : '#424242',
}))

const DeleteRecordModal = () => {
  const location = useLocation()
  const openModal = useSelector(state => state.modalRemoveTree.showModal)
  const treeId = useSelector(state => state.mapState.currentTreeId)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [comment, setComment] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [error, setError] = React.useState('')
  const [showSuccess, setShowSuccess] = React.useState(false)

  const TOKEN = `Bearer ${window.localStorage.getItem('ArbolEc_token')}`

  React.useEffect(() => {
    if (openModal) {
      setComment('')
      setError('')
      setIsSubmitting(false)
      setShowSuccess(false)
    }
  }, [openModal])

  const closeMapPopup = () => {
    try {
      // Cerrar popup usando Redux
      dispatch(setPopup({ open: false, code: null, id: null }))
    } catch (error) {
      console.log('Error al cerrar popup:', error)
    }
  }

  const getModalStyle = React.useMemo(() => {
    return {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: isMobile
        ? isSmallMobile
          ? 'calc(100vw - 24px)'
          : 'calc(100vw - 48px)'
        : 'min(560px, 90vw)',
      maxWidth: '100%',
      maxHeight: 'calc(100vh - 64px)',
      bgcolor: 'background.paper',
      boxShadow: 24,
      p: isMobile ? (isSmallMobile ? '20px' : '24px') : '28px',
      filter:
        'drop-shadow(0px 15px 12px rgba(0, 0, 0, 0.22)) drop-shadow(0px 19px 38px rgba(0, 0, 0, 0.3))',
      borderRadius: '16px',
      overflow: 'auto',
    }
  }, [isMobile, isSmallMobile])

  const handleCancelAction = () => {
    setComment('')
    setError('')
    dispatch(setStateModal(false))
  }

  const handleCommentChange = event => {
    const value = event.target.value
    setComment(value)
    if (error && value.trim().length >= 10) {
      setError('')
    }
  }

  function DeleteTree(reason) {
    return deleteTree(
      treeId,
      {
        reason,
      },
      TOKEN
    )
      .then(response => {
        return response?.delete ?? false
      })
      .catch(err => {
        console.log('Error al eliminar árbol:', err)
        throw err
      })
  }

  const handleContinueAction = async () => {
    const trimmedComment = comment.trim()

    if (!trimmedComment) {
      setError('El motivo de eliminación es obligatorio')
      return
    }

    if (trimmedComment.length < 10) {
      setError('El motivo debe tener al menos 10 caracteres')
      return
    }

    if (trimmedComment.length > 300) {
      setError('El motivo no puede exceder 300 caracteres')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const treeDeleted = await DeleteTree(trimmedComment)

      if (treeDeleted) {
        dispatch(setStateModal(false))
        closeMapPopup()

        const currentCity = detectCityFromURL(location.pathname) || 'loja'
        const baseRoute = buildCantonUrl(currentCity, cantones, `/${RT.MANAGEMENT_TREE}`)
        if (location.pathname.includes(RT.MANAGEMENT_TREE) && location.pathname !== baseRoute) {
          navigate(baseRoute, { replace: true })
        }

        dispatch(setEnabledFilter(false))
        dispatch(clearFilteredData())
        dispatch(invalidateMapData())

        setTimeout(() => {
          setShowSuccess(true)
        }, 500)
      } else {
        setError('No se pudo eliminar el árbol. Intente nuevamente.')
      }
    } catch (err) {
      console.log('Error durante eliminación:', err)
      setError('Error al eliminar el árbol. Intente nuevamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isValidComment = comment.trim().length >= 10 && comment.trim().length <= 300

  return (
    <>
      <Modal
        open={openModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        componentsProps={{ backdrop: { sx: { backdropFilter: 'blur(3px)' } } }}
        disableEscapeKeyDown>
        <Box sx={getModalStyle}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Warning sx={{ color: '#d32f2f', fontSize: 32 }} />
            <Typography istitle={1} id="modal-modal-title" variant="h5" component="h2">
              Eliminar Registro de Árbol
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography id="modal-modal-description" sx={{ mb: 2, lineHeight: 1.6 }}>
            <strong>Esta acción es irreversible.</strong> El árbol seleccionado será
            marcado como eliminado y ya no aparecerá en el mapa ni en los reportes. Los
            datos históricos se conservarán para fines de auditoría.
          </Typography>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Importante:</strong> Debe proporcionar un motivo válido para la
            eliminación.
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={isMobile ? 3 : 4}
            label="Motivo de eliminación *"
            placeholder="Ej: Árbol cortado por obras públicas, daño por tormenta, error en el registro, etc."
            value={comment}
            onChange={handleCommentChange}
            error={!!error}
            helperText={error || `${comment.length}/300 caracteres (mínimo 10)`}
            sx={{ mb: 2 }}
            disabled={isSubmitting}
            variant="outlined"
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3,
              flexDirection: isSmallMobile ? 'column-reverse' : 'row',
            }}>
            <Button
              onClick={handleCancelAction}
              variant="outlined"
              startIcon={<Cancel />}
              disabled={isSubmitting}
              sx={{
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                minWidth: isSmallMobile ? '100%' : '120px',
                py: 1.2,
              }}>
              Cancelar
            </Button>

            <Button
              onClick={handleContinueAction}
              variant="contained"
              color="error"
              startIcon={isSubmitting ? null : <Delete />}
              disabled={!isValidComment || isSubmitting}
              sx={{
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                minWidth: isSmallMobile ? '100%' : '120px',
                py: 1.2,
                fontWeight: 600,
              }}>
              {isSubmitting ? 'Eliminando...' : 'Confirmar Eliminación'}
            </Button>
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 2,
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}>
            Esta acción será registrada con fecha, hora y motivo para auditoría
          </Typography>
        </Box>
      </Modal>

      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}>
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          variant="filled"
          icon={<CheckCircle />}
          sx={{
            width: '100%',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}>
          ¡Árbol eliminado exitosamente! Los datos del mapa se han actualizado.
        </Alert>
      </Snackbar>
    </>
  )
}

export default DeleteRecordModal
